/**
 * A shelf of PDFs: covers, newest first, each opening into a window that scrolls.
 *
 * Two pages use this — the annual reports and the printed newsletters — and it is one
 * component rather than two copies of one because of the last twenty lines of it. Which
 * window a document opens in is not a styling choice: it is the same question the Worker
 * answers when it decides which edition of the file to serve, and a reader handed the
 * wrong window would be a reader handed the wrong file. There should be one place in the
 * client where that is decided, and this is it.
 *
 * Everything above those twenty lines is the shelf itself. No filters, no paging, no sort
 * control: a shelf is a few dozen covers on one page that scrolls, and every control it
 * could grow would be a control for finding something already on the screen.
 *
 * What the two shelves differ in is words — a report is addressed by its year and a
 * newsletter by the day it went out — so the words come in as props and nothing else
 * does. `describe` turns a row into the two lines on its card; `nameOf` turns it into the
 * phrase every sentence about it uses.
 */
import { useEffect, useState } from 'react'
import { useCanBrowse } from '../lib/viewmode'
import { GatedImage } from './GatedImage'
import { Card, Empty, Modal, Notice, QueryState, fmtBytes } from './ui'

/**
 * One cover on the shelf. A button, because opening it opens a dialog.
 *
 * A card that will not open has to say why on the card itself, since there is nothing
 * above the shelf to explain it. `available` is false for two unrelated reasons and they
 * need different words: a supporter is reading the covered edition and this one has none
 * prepared yet, or a member of staff is reading the printed one and no file was ever
 * stored. `anonymised` says which edition this account was handed, so it is what decides
 * the wording — telling staff a document is not ready for supporters would be a plain
 * falsehood about one that is simply missing.
 */
function ShelfCard({ row, base, describe, nameOf, onOpen }) {
  const { id, page_count, bytes, has_cover, available, anonymised } = row
  const { heading, sub } = describe(row)
  const why = anonymised ? 'Not yet prepared for supporters' : 'No file stored'
  return (
    <button
      type="button"
      className={`report-card${available ? '' : ' unavailable'}`}
      onClick={() => available && onOpen(row)}
      disabled={!available}
      title={
        available
          ? `Open ${nameOf(row)}`
          : anonymised
            ? `${nameOf(row)} has not been prepared for supporters yet`
            : `No file is stored for ${nameOf(row)}`
      }
    >
      <div className="report-cover">
        {has_cover ? (
          /* One request per cover, and a shelf has no paging — the whole shelf is on one
             page, which is thirty-eight covers on the newsletters today and three or four
             more every year. Drawn the same way as every other archive picture, which is
             to say a few at a time as the reader comes to them: see GatedImage, and
             lib/loadGate.js for the burst that made it necessary. */
          <GatedImage src={`/api/${base}/${id}/cover`} alt="" />
        ) : (
          /* No cover rendered for this one. Its heading stands in for it rather than a
             broken picture, and the card still opens. */
          <div className="report-cover-blank" aria-hidden="true">
            {heading}
          </div>
        )}
      </div>
      <div className="report-meta">
        <div className="report-year">{heading}</div>
        <div className="report-title truncate">{sub}</div>
        <div className="report-note">
          {available
            ? [page_count ? `${page_count} pages` : null, bytes ? fmtBytes(bytes) : null]
                .filter(Boolean)
                .join(' · ')
            : why}
        </div>
      </div>
    </button>
  )
}

/**
 * The document itself, in a window that scrolls, for anybody who may have the file.
 *
 * An <iframe> rather than a PDF renderer of our own: every browser this application is
 * used in has one built in, and it is better than anything that would fit in this file.
 * The link underneath is not decoration — iOS Safari draws only the first page of a PDF
 * in a frame, and on that browser the link is the whole feature.
 */
function ShelfFrame({ row, base, title, onClose }) {
  return (
    <Modal
      open={!!row}
      onClose={onClose}
      size="xl"
      title={title}
      footer={
        row && (
          <>
            <a
              className="btn btn-primary"
              href={`/api/${base}/${row.id}/file`}
              target="_blank"
              rel="noreferrer"
            >
              Open in a new tab
            </a>
            <button className="btn" onClick={onClose}>
              Close
            </button>
          </>
        )
      }
    >
      {row && (
        <iframe
          className="report-frame"
          src={`/api/${base}/${row.id}/file#view=FitH`}
          title={title}
        />
      )}
    </Modal>
  )
}

/**
 * The same document for a reader of the covered edition: pictures of its pages, no file.
 *
 * Who that is — a donor at the other door, a view-only staff account at this one — makes
 * no difference and is not asked. Both are shown the archive with the faces covered, and
 * both are welcome to read every word without being given a copy. The browser's own PDF
 * viewer cannot make that distinction: it comes with a download button, a print button
 * and a URL that opens in a tab. So the file never reaches it. The bytes are fetched by
 * this component, drawn to pictures by pdf.js, and the pictures are what goes on the
 * screen. Printing is refused by a rule in the stylesheet, and the Worker will not hand
 * the file to a tab at all (reportIsForReadingOnly).
 *
 * WHAT IT DOES NOT DO, said here so nobody later mistakes it for more than it is: it does
 * not stop a determined person keeping a copy, and nothing that renders a document in a
 * browser can. The pages are pictures on a screen; they can be photographed, and a reader
 * who opens the developer tools can save them. This closes the doors people actually walk
 * through — Download, Print, "open in new tab", "save link as" — and leaves the archive
 * relying, as it did before, on covering the faces in the first place.
 */
function ShelfPages({ row, base, noun, title, onClose }) {
  const [pages, setPages] = useState([])
  const [state, setState] = useState('loading')
  const [progress, setProgress] = useState(null)

  // Keyed on the row by its parent, so opening a different document mounts this afresh
  // rather than resetting three pieces of state on the way in. One document, one
  // lifetime, one set of object URLs to release when it ends.
  useEffect(() => {
    if (!row) return undefined
    const control = new AbortController()
    const made = []

    ;(async () => {
      try {
        const res = await fetch(`/api/${base}/${row.id}/file`, {
          credentials: 'same-origin',
          signal: control.signal,
        })
        if (!res.ok) throw new Error(`The ${noun} could not be opened (${res.status})`)
        const data = new Uint8Array(await res.arrayBuffer())
        const { renderReportPages } = await import('../lib/reportPdf')
        await renderReportPages(data, {
          signal: control.signal,
          onPage: ({ url, page, total }) => {
            made.push(url)
            setProgress({ page, total })
            setPages((p) => [...p, url])
          },
        })
        if (!control.signal.aborted) setState('ready')
      } catch (err) {
        if (!control.signal.aborted) setState(err.message || `The ${noun} could not be opened`)
      }
    })()

    return () => {
      control.abort()
      // The pages are object URLs. Left alone they hold their blobs for the life of the
      // tab, which for a reader working along the shelf is everything they opened.
      for (const url of made) URL.revokeObjectURL(url)
    }
  }, [row, base, noun])

  return (
    <Modal
      open={!!row}
      onClose={onClose}
      size="xl"
      title={title}
      footer={
        <>
          {/* In the footer rather than under the pages, where it sat below the fold of a
              scrolling window and was read by nobody. This is the sentence that explains
              why there is no Download button to look for. */}
          <span className="small subtle report-pages-foot">
            This {noun} is here to read. It is not available to download or print.
          </span>
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div
        className="report-pages"
        /* The right-click menu on a picture is one of the doors this is closing, and it
           is the only one that has to be closed here rather than in the Worker. */
        onContextMenu={(e) => e.preventDefault()}
      >
        {pages.map((url, i) => (
          <img key={url} src={url} alt={`Page ${i + 1} of ${title}`} draggable={false} />
        ))}
        {state === 'loading' && (
          <div className="report-pages-note">
            {progress
              ? `Drawing page ${progress.page} of ${progress.total}…`
              : `Opening the ${noun}…`}
          </div>
        )}
        {state !== 'loading' && state !== 'ready' && (
          <Notice tone="danger" icon="warning" title={`This ${noun} could not be opened`}>
            {state}
          </Notice>
        )}
      </div>
    </Modal>
  )
}

export default function PdfShelf({
  query,
  base,
  noun,
  icon,
  shelfTitle = 'The Shelf',
  describe,
  nameOf,
  windowTitle,
  emptyTitle,
  emptyBody,
}) {
  const canBrowse = useCanBrowse()
  const [open, setOpen] = useState(null)
  // Which of the two windows a document opens in, off the one fact that also decides
  // which edition the Worker serves — so the window a reader gets and the file they are
  // allowed cannot come apart. This is not the control. The Worker makes the same
  // decision for the file itself, and a reader sent the wrong window would find an empty
  // frame rather than a way round anything.
  const Viewer = canBrowse ? ShelfFrame : ShelfPages

  return (
    <>
      <div className="col">
        <QueryState query={query}>
          {(d) => (
            <Card
              title={shelfTitle}
              sub={`${d.rows.length} ${noun}${d.rows.length === 1 ? '' : 's'}`}
            >
              {!d.rows.length ? (
                <Empty icon={icon} title={emptyTitle}>
                  {emptyBody}
                </Empty>
              ) : (
                <div className="report-shelf">
                  {d.rows.map((r) => (
                    <ShelfCard
                      key={r.id}
                      row={r}
                      base={base}
                      describe={describe}
                      nameOf={nameOf}
                      onOpen={setOpen}
                    />
                  ))}
                </div>
              )}
            </Card>
          )}
        </QueryState>
      </div>
      <Viewer
        key={open ? open.id : 'closed'}
        row={open}
        base={base}
        noun={noun}
        title={open ? windowTitle(open) : ''}
        onClose={() => setOpen(null)}
      />
    </>
  )
}
